import React, { useState } from "react";
import { VanillaMapProps, VanillaMap } from "./";
import { Marker, Polygon } from "@react-google-maps/api";
import { Button, Menu, MenuItem, Typography } from "@material-ui/core";
import "./styles.scss";
import { Area } from "./interfaces";
import AreaPolygon from "./AreaPolygon";

export interface DrawingMapProps extends VanillaMapProps {
  color?: string;
  areas?: Area[];
  displayOnlyAreas?: Area[];
  useAreaNames?: boolean;
  onAreaAdded?: (newArea: Area) => void;
  onAreaRemoved?: (position: number, area: Area) => void;
  // onAreasChange?: (areas: Area[]) => void;
}

const DrawingMap: React.FC<DrawingMapProps> = ({
  color = "red",
  onAreaRemoved,
  onAreaAdded,
  useAreaNames,
  displayOnlyAreas = [],
  areas = [],
  ...props
}) => {
  const [draftPoints, setDraftPoints] = useState<Area["points"]>([]);
  const [menuAnchor, setMenuAnchor] = useState<{
    x: number;
    y: number;
    item: number;
  } | null>(null);

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const onDeleteClick = () => {
    if (menuAnchor) {
      if (onAreaRemoved) onAreaRemoved(menuAnchor.item, areas[menuAnchor.item]);
    }
    setMenuAnchor(null);
  };
  const onAreaRightClick = (position: number, area: Area) => () => {
    if (onAreaRemoved) onAreaRemoved(position, area);
  };

  const onMapClick = (point: { latitude: number; longitude: number }) => {
    setDraftPoints(points => [...points, point]);
    if (props.onMapClick) props.onMapClick(point);
  };

  const finishArea = () => {
    if (draftPoints.length < 3) return;
    if (onAreaAdded) onAreaAdded({ points: draftPoints, name: "" });
    setDraftPoints([]);
  };

  return (
    <div
      onContextMenu={e => {
        e.preventDefault();
      }}
      style={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      {!props.mapOnly && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 8
          }}
        >
          <Typography variant="body2">
            Click the map to add corners, then finish the area.
          </Typography>
          <Button
            color="primary"
            variant="contained"
            size="small"
            disabled={draftPoints.length < 3}
            onClick={finishArea}
          >
            Finish area
          </Button>
          <Button
            size="small"
            disabled={draftPoints.length === 0}
            onClick={() => setDraftPoints(points => points.slice(0, -1))}
          >
            Undo point
          </Button>
          <Button
            size="small"
            disabled={draftPoints.length === 0}
            onClick={() => setDraftPoints([])}
          >
            Cancel
          </Button>
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0 }}>
        <VanillaMap
          {...props}
          disableMarking={true}
          areas={displayOnlyAreas}
          onMapClick={props.mapOnly ? props.onMapClick : onMapClick}
        >
          <Menu
            keepMounted
            open={!!menuAnchor}
            onClose={handleMenuClose}
            anchorReference="anchorPosition"
            anchorPosition={
              menuAnchor ? { top: menuAnchor.y, left: menuAnchor.x } : undefined
            }
          >
            <MenuItem onClick={onDeleteClick}>Delete</MenuItem>
          </Menu>

          <>
            {draftPoints.length > 0 && (
              <Polygon
                paths={draftPoints.map(point => ({
                  lat: point.latitude,
                  lng: point.longitude
                }))}
                options={{
                  clickable: false,
                  fillColor: color,
                  strokeColor: color
                }}
              />
            )}

            {draftPoints.map((point, index) => (
              <Marker
                key={`${point.latitude}-${point.longitude}-${index}`}
                position={{ lat: point.latitude, lng: point.longitude }}
                label={{
                  text: String(index + 1),
                  color: "white",
                  fontSize: "10px"
                }}
                icon={{
                  path: google.maps.SymbolPath.CIRCLE,
                  scale: 8,
                  fillColor: color,
                  fillOpacity: 1,
                  strokeColor: "white",
                  strokeWeight: 2
                }}
              />
            ))}

            {areas.map((area, i) => (
              <AreaPolygon
                key={area.points[0].latitude}
                area={area}
                altName={`Area ${i + 1}`}
                onRightClick={onAreaRightClick(i, area)}
                color={color}
                useAreaNames={useAreaNames}
              />
            ))}
          </>
        </VanillaMap>
      </div>
    </div>
  );
};

export default DrawingMap;
